import { useState } from "react";
import { Box, Button, Divider, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ActionArgField from "./ActionArgField";
import ConditionEditor from "./ConditionEditor";
import HelpHint from "../components/HelpHint";
import type { ConditionFieldDef, NodeDef, WorkflowEdge, WorkflowNode } from "./workflow.types";

interface NodePropertiesPanelProps {
  node: WorkflowNode;
  def?: NodeDef;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  nodeDefsById: Record<string, NodeDef>;
  conditionFields: ConditionFieldDef[];
  onChange: (patch: Partial<WorkflowNode["config"]>) => void;
  onClose: () => void;
  /** The workflow's own trigger.config (not this canvas node's config.args) — only meaningful for a trigger node with configFields. */
  triggerConfig?: Record<string, unknown>;
  onChangeTriggerConfig?: (config: Record<string, unknown>) => void;
}

export default function NodePropertiesPanel({
  node,
  def,
  nodes,
  edges,
  nodeDefsById,
  conditionFields,
  onChange,
  onClose,
  triggerConfig,
  onChangeTriggerConfig,
}: NodePropertiesPanelProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const basicFields = (def?.argFields || []).filter((f) => !f.advanced);
  const advancedFields = (def?.argFields || []).filter((f) => f.advanced);

  // Dynamic per node — never a fixed string, so it never claims a feature
  // (e.g. skills) that this specific node doesn't actually have. Kept short:
  // just the node's own one-line description, plus a terse skill nudge only
  // when this node actually has a skills field.
  const hasSkillsField = (def?.argFields || []).some((f) => f.type === "select-skills");
  const helpText = [def?.description, hasSkillsField ? "Skills below shape tone — or type /skill in chat." : null]
    .filter(Boolean)
    .join(" ");

  /** Current resolved value of any argField on this node, by key — used to read a `dependsOn` sibling's value. */
  const getArgValue = (key: string): string =>
    node.config.args?.[key] ?? def?.argFields?.find((f) => f.key === key)?.defaultTemplate ?? "";

  return (
    <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          {node.type === "trigger" ? "Trigger" : node.type === "condition" ? "Condition" : "Action"}
        </Typography>
        {helpText && <HelpHint text={helpText} />}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {node.type === "trigger" && (
        <>
          <TextField
            label="Step label"
            fullWidth
            size="small"
            value={node.config.label ?? def?.label ?? ""}
            onChange={(e) => onChange({ label: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {def?.description || "This workflow starts when this event occurs."}
          </Typography>
          {(def?.configFields || []).map((field) => {
            // Stored on trigger.config as a real string[] (not the comma-joined
            // string ActionArgField's multi-select fields use) — convert both ways.
            const stored = triggerConfig?.[field.key];
            const value = Array.isArray(stored) ? stored.join(",") : field.defaultTemplate;
            return (
              <ActionArgField
                key={field.key}
                field={field}
                value={value}
                onChange={(v) =>
                  onChangeTriggerConfig?.({
                    ...triggerConfig,
                    [field.key]: v.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            );
          })}
        </>
      )}

      {node.type === "action" && (
        <>
          <TextField
            label="Step label"
            fullWidth
            size="small"
            value={node.config.label ?? def?.label ?? ""}
            onChange={(e) => onChange({ label: e.target.value })}
            sx={{ mb: 2 }}
          />
          {basicFields.map((field) => (
            <ActionArgField
              key={field.key}
              field={field}
              value={node.config.args?.[field.key] ?? field.defaultTemplate}
              scopeValue={field.dependsOn ? getArgValue(field.dependsOn) : undefined}
              onChange={(v, extra) => onChange({ args: { ...node.config.args, [field.key]: v, ...extra } })}
            />
          ))}
          {advancedFields.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setShowAdvanced((s) => !s)}
                sx={{ mb: 1, textTransform: "none" }}
              >
                {showAdvanced ? "Hide advanced options" : "Show advanced options"}
              </Button>
              {showAdvanced &&
                advancedFields.map((field) => (
                  <ActionArgField
                    key={field.key}
                    field={field}
                    value={node.config.args?.[field.key] ?? field.defaultTemplate}
                    scopeValue={field.dependsOn ? getArgValue(field.dependsOn) : undefined}
                    onChange={(v, extra) => onChange({ args: { ...node.config.args, [field.key]: v, ...extra } })}
                  />
                ))}
            </>
          )}
        </>
      )}

      {node.type === "condition" && (
        <ConditionEditor
          node={node}
          nodes={nodes}
          edges={edges}
          nodeDefsById={nodeDefsById}
          conditionFields={conditionFields}
          onChange={onChange}
        />
      )}
    </Box>
  );
}
